import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { AccuracyRunRow } from './AccuracyRunRow';
import { useAccuracyRunsModal } from './useAccuracyRunsModal';

interface AccuracyRunsModalProps {
  open: boolean;
  projectId: string | null;
  projectName: string | null;
  onClose: () => void;
  onNewRun: (projectId: string) => void;
}

/**
 * Run history for one project's accuracy runs, opened from the Accuracy job cell.
 * Data fetching, the cancel mutation and deep-link navigation live in
 * `useAccuracyRunsModal`; this component and `AccuracyRunRow` are presentational.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const AccuracyRunsModal = ({
  open,
  projectId,
  projectName,
  onClose,
  onNewRun,
}: AccuracyRunsModalProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const {
    runs,
    isLoading,
    error,
    refetch,
    isCancelPending,
    cancelError,
    dismissCancelError,
    subtitle,
    handleClose,
    handleNewRun,
    handleViewResults,
    handleCancel,
  } = useAccuracyRunsModal({ open, projectId, projectName, onClose, onNewRun });

  const renderBody = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: theme.spacing(10) }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {formatMessage({ id: 'adminProjects.accuracyRunsModal.retry' })}
            </Button>
          }
        >
          {formatMessage({ id: 'adminProjects.accuracyRunsModal.loadError' })}
        </Alert>
      );
    }

    if (runs.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: theme.spacing(6) }}>
          {formatMessage({ id: 'adminProjects.accuracyRunsModal.empty' })}
        </Typography>
      );
    }

    return (
      <Box>
        {runs.map((view) => (
          <AccuracyRunRow
            key={view.run.accuracyJobId}
            view={view}
            onViewResults={handleViewResults}
            onCancel={handleCancel}
            isCancelPending={isCancelPending}
          />
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing(4) }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">
            {formatMessage({ id: 'adminProjects.accuracyRunsModal.title' })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<PlayCircleOutlineIcon />}
          onClick={handleNewRun}
          disabled={!projectId}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          {formatMessage({ id: 'adminProjects.accuracyRunsModal.newRun' })}
        </Button>

        <IconButton
          size="small"
          onClick={handleClose}
          aria-label={formatMessage({ id: 'adminProjects.accuracyRunsModal.close' })}
          sx={{ flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {cancelError && (
          <Alert severity="error" sx={{ mb: theme.spacing(3) }} onClose={dismissCancelError}>
            {formatMessage({ id: 'adminProjects.accuracyRunsModal.cancelError' })}
          </Alert>
        )}
        {renderBody()}
      </DialogContent>
    </Dialog>
  );
};
